import { DEMO_EXPERTS } from './experts';

// Simple query topic templates
const QUERY_TOPICS = [
  { title: 'Digital Marketing Trends 2026', topics: ['Digital Marketing', 'SEO', 'Content Marketing'], outlet: 'Marketing Week' },
  { title: 'Social Media Strategy for Tech Startups', topics: ['Social Media', 'Tech Startups', 'Brand Strategy'], outlet: 'TechCrunch' },
  { title: 'E-commerce SEO Best Practices', topics: ['E-commerce', 'SEO', 'Technical SEO'], outlet: 'Search Engine Journal' },
  { title: 'Remote Work and Team Productivity', topics: ['Remote Work', 'Productivity', 'Management'], outlet: 'Forbes' },
  { title: 'AI in Marketing Automation', topics: ['AI', 'Machine Learning', 'Marketing Automation'], outlet: 'Wired' },
  { title: 'Content Marketing ROI Measurement', topics: ['Content Marketing', 'ROI', 'Analytics'], outlet: 'Content Marketing Institute' },
  { title: 'Local SEO for Small Businesses', topics: ['Local SEO', 'Small Business', 'Google My Business'], outlet: 'Search Engine Land' },
  { title: 'Email Marketing Best Practices', topics: ['Email Marketing', 'Automation', 'Conversion'], outlet: 'MarketingProfs' },
  { title: 'Influencer Marketing ROI', topics: ['Influencer Marketing', 'Social Media', 'ROI'], outlet: 'Adweek' },
  { title: 'Video Marketing Trends', topics: ['Video Marketing', 'TikTok', 'YouTube'], outlet: 'Social Media Today' },
  { title: 'B2B Lead Generation Strategies', topics: ['B2B', 'Lead Generation', 'Marketing'], outlet: 'Inc. Magazine' },
  { title: 'PPC Campaign Optimization', topics: ['PPC', 'Google Ads', 'Paid Advertising'], outlet: 'Entrepreneur' },
  { title: 'Brand Strategy for Startups', topics: ['Brand Strategy', 'Startups', 'Marketing'], outlet: 'Fast Company' },
  { title: 'Data Analytics in Marketing', topics: ['Analytics', 'Data', 'Marketing'], outlet: 'HubSpot Blog' },
  { title: 'Customer Experience Optimization', topics: ['Customer Experience', 'CX', 'Digital'], outlet: 'CMSWire' },
];

const getActiveExperts = () => DEMO_EXPERTS.filter((e) => e.isActive === 1);

const createPitch = (expert, query, topics) => {
  const topicStr = topics.join(', ').toLowerCase();
  return `Hi ${expert.name.split(' ')[0]},

Happy Monday! I came across an opportunity with ${query.outlet} about ${topicStr}. Given your experience as ${expert.position} at ${
    expert.company
  }, this would be a great fit for you. I've prepared some draft responses below - feel free to edit or add your insights!

The deadline for this opportunity is ${query.dueDate}.
------------------------------------------------
- Emphasize the symbiotic relationship between PR and SEO, noting how effective SEO can amplify PR efforts by increasing the visibility of press releases and media coverage.
- Highlight innovative practices such as leveraging SEO tools to analyze keywords and search trends, guiding the creation of press materials that resonate with target audiences.
- Discuss the importance of integrating SEO strategies in crisis management to control the narrative online, ensuring that positive content is prioritized in search results.
- Share insights on using SEO for reputation management by regularly monitoring brand mentions and optimizing positive content to maintain a favorable online presence.
- Illustrate the role of SEO in enhancing content strategy, ensuring that PR content is not only engaging but also optimized for search engines to reach a wider audience.
- Offer practical advice on collaborating with SEO specialists and PR teams to craft a cohesive digital strategy, enhancing brand management and media relations.
- Use personal experience and success stories from seoplus+ to demonstrate the impact of a well-integrated SEO and PR approach on achieving digital marketing goals.
- Address the evolving role of AI and automation in digital marketing, emphasizing how these technologies can enhance both SEO and PR efforts, making them more efficient and data-driven.
- Conclude with actionable tips on staying updated with SEO trends and continuously adapting PR strategies to maintain competitive advantage and optimize online presence.
------------------------------------------------
Query: ${query.title}
Outlet: ${query.outlet}
Deadline: ${query.dueDate}
------------------------------------------------
Let me know if you'd like to add or adjust anything!

Best regards,
Catherine`;
};

const createReporterSubmission = (expert, query, topics) => {
  const response = `Expert insights on ${topics.join(', ').toLowerCase()} from industry professional.`;

  return `Hi Rebecca,

I hope your day is going well!

I am reaching out on behalf of ${expert.name}, ${expert.position} at ${expert.company} (${expert.websiteURL || 'https://seoplus.com/'}). ${expert.bio}

${expert.name} was happy to offer some insight into your query, as can be seen below:
-----------------------------
${response}
-----------------------------
Let me know if you have any additional questions, ${expert.name} is always happy to help!

Thank you very much for your time,

Catherine`;
};

// Generator functions for each status type
export const generateQuery = (id, topicIndex, status, config = {}) => {
  const topic = QUERY_TOPICS[topicIndex % QUERY_TOPICS.length];
  const activeExperts = getActiveExperts();
  const expert = config.expertID ? DEMO_EXPERTS.find((e) => e.id === config.expertID) : activeExperts[id % activeExperts.length];

  // Vary userClaimID: 40% unclaimed (null), 30% user 1, 30% user 2
  const userClaimID =
    config.userClaimID !== undefined
      ? config.userClaimID
      : (() => {
          const rand = id % 10;
          if (rand < 4) return 6; // 40% unclaimed
          if (rand < 7) return 9; // 30% user 1
          return 5; // 30% user 2
        })();

  // Generate dates dynamically
  // Spread queries across last 45 days (covering this month and last month)
  const today = new Date();
  const daysAgo = id % 45; // Spread across last 45 days
  const baseDate = new Date(today);
  baseDate.setDate(baseDate.getDate() - daysAgo);

  // Due date is 7-14 days after pull date
  const dueDate = new Date(baseDate);
  dueDate.setDate(dueDate.getDate() + 7 + (id % 8));

  const baseQuery = {
    id,
    title: topic.title,
    description: `Seeking expert commentary on ${topic.topics.join(', ').toLowerCase()}. ${
      config.description || 'Looking for insights from industry professionals.'
    }`,
    source: id % 2 === 0 ? 'Qwoted' : 'HARO',
    queryURL: `https://qwoted.com/queries/${10000 + id}`,
    mediaOutlet: topic.outlet,
    dueDate: dueDate.toISOString().split('T')[0],
    topics: JSON.stringify(topic.topics),
    qwotedTags: topic.topics.map((t) => ({ hashtag: `#${t.replace(/\s+/g, '')}` })),
    createdBy: 1,
    pullTime: baseDate.toISOString(),
    createdAt: baseDate.toISOString(),
    updatedAt: baseDate.toISOString(),
    ...config,
  };

  const inboxNotes = (noteText, isDraft = false) =>
    JSON.stringify({
      notes: noteText,
      dateWriting: isDraft ? '2026-02-20T12:00:00.000Z' : '2026-02-18T12:00:00.000Z',
      reportDate: null,
      draftCompany: null,
    });
  const draftDueDate = '2026-02-24';
  const sentDueDate = '2026-02-20';

  // Status-specific configuration
  const statusConfigs = {
    new: {
      status: 'new',
      expertsID: null,
      emailSentOut: 0,
      userClaimID: null,
      notes: '',
      mediaURL: null,
      leads: [],
      sentToExpertDate: null,
      sentToReporterDate: null,
    },
    draft: {
      status: 'draft',
      expertsID: expert.id,
      emailSentOut: 0,
      userClaimID,
      dueDate: draftDueDate,
      notes: inboxNotes(`${expert.name} - drafting response`, true),
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'draft',
          sendToExpert: 0,
          returnedEmailFromExpert: 0,
          sendToQuery: 0,
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: draftDueDate }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: draftDueDate }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: null,
      sentToReporterDate: null,
    },
    claimed: {
      status: 'claimed',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      dueDate: sentDueDate,
      notes: inboxNotes(`${expert.name} assigned`),
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'pending',
          sendToExpert: 1,
          returnedEmailFromExpert: 0,
          sendToQuery: 0,
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: null,
    },
    awaiting: {
      status: 'awaiting',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      dueDate: sentDueDate,
      notes: inboxNotes(`Sent to ${expert.name}`),
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'awaiting',
          sendToExpert: 1,
          returnedEmailFromExpert: 0,
          sendToQuery: 0,
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: null,
    },
    pending: {
      status: 'pending',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      notes: 'Submitted to reporter',
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'pending',
          sendToExpert: 1,
          returnedEmailFromExpert: 1,
          sendToQuery: 1,
          currentQueryText: createReporterSubmission(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: new Date(baseDate.getTime() + 86400000 * 3).toISOString(),
    },
    successful: {
      status: 'successful',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      notes: 'Article published!',
      mediaURL: `https://${topic.outlet.toLowerCase().replace(/\s+/g, '')}.com/article-${id}`,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'successful',
          sendToExpert: 1,
          returnedEmailFromExpert: 1,
          sendToQuery: 1,
          currentQueryText: createReporterSubmission(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: new Date(baseDate.getTime() + 86400000 * 3).toISOString(),
    },
    repurposed: {
      status: 'repurposed',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      notes: 'Marking for repurpose',
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'repurposed',
          sendToExpert: 1,
          returnedEmailFromExpert: 1,
          sendToQuery: 1,
          currentQueryText: createReporterSubmission(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: new Date(baseDate.getTime() + 86400000 * 3).toISOString(),
    },
    journalistPassed: {
      status: 'journalistPassed',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      notes: 'Journalist passed on this opportunity',
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'journalistPassed',
          sendToExpert: 1,
          returnedEmailFromExpert: 1,
          sendToQuery: 1,
          currentQueryText: createReporterSubmission(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: new Date(baseDate.getTime() + 86400000 * 3).toISOString(),
    },
    clientApproved: {
      status: 'clientApproved',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      dueDate: sentDueDate,
      notes: inboxNotes('Client approved - ready to submit'),
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'clientApproved',
          sendToExpert: 1,
          returnedEmailFromExpert: 1,
          sendToQuery: 0,
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: null,
    },
    clientNeedsEdits: {
      status: 'clientNeedsEdits',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      dueDate: sentDueDate,
      notes: inboxNotes(`Needs edits from ${expert.name}`),
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'clientNeedsEdits',
          sendToExpert: 1,
          returnedEmailFromExpert: 1,
          sendToQuery: 0,
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: null,
    },
    needsFollowUp: {
      status: 'needsFollowUp',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      dueDate: sentDueDate,
      notes: inboxNotes(`Follow up needed with ${expert.name}`),
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'needsFollowUp',
          sendToExpert: 1,
          returnedEmailFromExpert: 0,
          sendToQuery: 0,
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: sentDueDate }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: null,
    },
    pendingPublication: {
      status: 'pendingPublication',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      notes: 'Pending publication',
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'pendingPublication',
          sendToExpert: 1,
          returnedEmailFromExpert: 1,
          sendToQuery: 1,
          currentQueryText: createReporterSubmission(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: new Date(baseDate.getTime() + 86400000 * 3).toISOString(),
    },
    paidEditorial: {
      status: 'paidEditorial',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      notes: 'Paid editorial opportunity',
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'paidEditorial',
          sendToExpert: 1,
          returnedEmailFromExpert: 1,
          sendToQuery: 1,
          currentQueryText: createReporterSubmission(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: new Date(baseDate.getTime() + 86400000 * 3).toISOString(),
    },
    clientPassed: {
      status: 'clientPassed',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      notes: 'Client passed on opportunity',
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'clientPassed',
          sendToExpert: 1,
          returnedEmailFromExpert: 1,
          sendToQuery: 0,
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: null,
    },
    inquireInterest: {
      status: 'inquireInterest',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      notes: 'Sent for client insight/interest',
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'inquireInterest',
          sendToExpert: 1,
          returnedEmailFromExpert: 0,
          sendToQuery: 0,
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: null,
    },
    interviewOpportunity: {
      status: 'interviewOpportunity',
      expertsID: expert.id,
      emailSentOut: 100 + id,
      userClaimID,
      notes: 'Interview opportunity',
      mediaURL: null,
      leads: [
        {
          id: 100 + id,
          queryID: id,
          expertID: expert.id,
          status: 'interviewOpportunity',
          sendToExpert: 1,
          returnedEmailFromExpert: 0,
          sendToQuery: 0,
          initialSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          currentSendToExpert: createPitch(expert, { ...topic, dueDate: dueDate.toISOString().split('T')[0] }, topic.topics),
          createdAt: baseDate.toISOString(),
        },
      ],
      sentToExpertDate: baseDate.toISOString(),
      sentToReporterDate: null,
    },
    discarded: {
      status: 'discarded',
      expertsID: null,
      emailSentOut: 0,
      userClaimID: null,
      notes: 'Not a good fit for our experts',
      mediaURL: null,
      leads: [],
      sentToExpertDate: null,
      sentToReporterDate: null,
    },
  };

  return {
    ...baseQuery,
    ...statusConfigs[status],
  };
};

// Helper to generate multiple queries
export const generateQueries = (configs) => {
  return configs.map(({ id, topicIndex, status, expertID }) => generateQuery(id, topicIndex, status, { expertID }));
};
